import { TaskResponse } from "@9amhealth/openapi";
import { useBloc, useBlocNext } from "@blac/react";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import Countdown from "components/Countdown/Countdown";
import Translate from "components/Translate/Translate";
import React, { FC, useId, useLayoutEffect, useMemo, useRef } from "react";
import { Button } from "react-aria-components";
import leftPadNumber from "src/lib/leftPadNumber";
import translate from "src/lib/translate";
import AppointmentsBloc from "src/state/AppointmentsBloc/AppointmentsBloc";
import {
  AppPopup,
  useAppQueryPopup
} from "../AppQueryPopups/AppQueryPopupsBloc";
import useQuestionnaireTracking from "../CustomQuestionnairePreview/useQuestionnaireTracking";
import { SkipOrScheduleSheet } from "../SkipOrScheduleSheet/SkipOrScheduleSheet";
import { JourneyModuleTriggerBloc } from "./JourneyModuleTriggerBloc";
import {
  ButtonVisual,
  VisualIcon,
  VisualOverlay,
  VisualText
} from "./ModuleButtonComponents";
import Spacer from "./Spacer";
import UserJourneyBloc, {
  UserJourneyLesson,
  UserJourneyLessonItem,
  UserJourneyModule
} from "./UserJourneyBloc";

const wiggleLocked = keyframes`
  0% { transform: translateX(0); }
  15% { transform: translateX(0.66rem); }
  30% { transform: translateX(-0.5rem); }
  45% { transform: translateX(0.44rem); }
  60% { transform: translateX(-0.32rem); }
  75% { transform: translateX(0.3rem); }
  90% { transform: translateX(-0.1rem); }
  100% { transform: translateX(0); }
`;

const openingScaleUp = keyframes`
  0% { transform: scale(1); }
  40% { opacity: 0; }
  50% {
    transform: scale(2);
    opacity: 0;
  }
  90% {
    transform: scale(1);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

export const ButtonWrap = styled(Button)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: transparent;
  border: none;
  width: 100%;
  outline: none;

  &[transition="LOCKED"] {
    .button-background {
      animation: ${wiggleLocked} 0.8s ease-in-out;
    }
  }

  &[transition="AVAILABLE"],
  &[transition="COMPLETED"],
  &[transition="IN_PROGRESS"],
  &[transition="SKIPPED"] {
    .button-background {
      animation: ${openingScaleUp} 1.2s ease-in-out;
    }
  }
`;

const ModuleName = styled.div`
  margin-top: 0.5rem;
  color: var(--Greys-Charcoal, #212121);
  text-align: center;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 500;
  letter-spacing: -0.0225rem;
  padding: 0 1.5rem;
  line-height: 1.4;
  text-wrap: pretty;
`;

const ModuleStatus = styled.div`
  display: flex;
  gap: 0.2rem;
`;

const JourneyModuleTrigger: FC<{
  item: UserJourneyLessonItem;
  lesson: UserJourneyLesson;
  module: UserJourneyModule;
  onTaskStatusUpdated: (taskStatus: string) => void;
}> = (props) => {
  useQuestionnaireTracking();

  const [{ enableAll }, { tasks, questionnaireTasks }] =
    useBloc(UserJourneyBloc);
  const [{ scheduledAppointments }, { getAppointment, getAppointmentTime }] =
    useBloc(AppointmentsBloc);

  const randomId = useId();
  const propsTask = props.item.task;
  const fullTaskSlug = propsTask
    ? `${propsTask.program}-${propsTask.group}-${propsTask.slug}`
    : randomId;

  const [openTypeformPopup, closeTypeformPopup] = useAppQueryPopup(
    AppPopup.lifestyleProgram
  );
  const [openModuleRatingPopup, closeModuleRatingPopup] = useAppQueryPopup(
    AppPopup.questionnaire
  );
  const [openAppointmentDetailsPopup] = useAppQueryPopup(AppPopup.appointment);
  const [openSchedulerPopup] = useAppQueryPopup(AppPopup.iframe);
  const refButton = useRef<HTMLButtonElement>(null);

  const [
    { isSkipSheetOpen },
    {
      isQuestionnaire,
      questionnaireIndex,
      isCoaching,
      isCompleted,
      isSkipped,
      isInProgress,
      isAvailable,
      appointmentType,
      coachingInfoAvailable,
      item,
      task,
      lesson,
      module,
      moduleStartingDate,
      triggerTaskAction,
      handleSheetClose,
      handleSheetSkip,
      handleSheetContinue,
      setRefButton
    }
  ] = useBlocNext(JourneyModuleTriggerBloc, {
    id: fullTaskSlug,
    props: {
      item: props.item,
      lesson: props.lesson,
      module: props.module,
      onTaskStatusUpdated: props.onTaskStatusUpdated,
      questionnaireTasks,
      enableAll,
      popups: {
        openTypeformPopup,
        closeTypeformPopup,
        openModuleRatingPopup,
        closeModuleRatingPopup,
        openAppointmentDetailsPopup,
        openSchedulerPopup
      }
    },
    onMount: (bloc) => {
      bloc.init();
    }
  });

  useLayoutEffect(() => {
    setRefButton(refButton);
  }, [refButton]);

  const appointmentId = task?.additionalData?.appointment_id as string;
  const appointment = coachingInfoAvailable
    ? getAppointment(appointmentId, scheduledAppointments)
    : undefined;
  const { start, end } = appointment ?? {};
  const { weekday, startTime, endTime, timeZone } = getAppointmentTime(
    start,
    end
  );

  const visualText = isQuestionnaire
    ? leftPadNumber(questionnaireIndex + 1)
    : "";

  const animateSpacerAfter = useMemo(() => {
    const slug = task?.slug;
    if (!slug) return false;

    const taskIndex = tasks.findIndex((t) => t?.slug === slug);
    if (taskIndex === -1) return false;

    const nextTask = tasks[taskIndex + 1];
    if (!nextTask) return false;

    const nextTaskStatus = nextTask.status;
    const nextTaskIsCoaching = nextTask.slug.includes("coaching");

    return (
      nextTaskStatus !== TaskResponse.status.LOCKED ||
      ((isCompleted || isSkipped) && nextTaskIsCoaching)
    );
  }, [task?.slug]);

  const isLast =
    module.lessons.indexOf(lesson) === module.lessons.length - 1 &&
    lesson.items.indexOf(item) === lesson.items.length - 1;

  return (
    <ButtonWrap onPress={triggerTaskAction} ref={refButton}>
      <ButtonVisual item={item} module={module}>
        <VisualText>{visualText}</VisualText>
        <VisualOverlay />
        <VisualIcon className="button-icon" status={item.task?.status} />
      </ButtonVisual>
      <ModuleName>
        {isQuestionnaire
          ? lesson.name
          : translate("coachingSession", { context: appointmentType })}
      </ModuleName>
      {isQuestionnaire && item.task?.status && (
        <ModuleStatus className="as-little">
          {item.task.status === TaskResponse.status.LOCKED &&
          moduleStartingDate ? (
            <>
              <Translate msg="journey.item.unlocksIn" />
              <Countdown to={moduleStartingDate.toDate()} />
            </>
          ) : (
            translate("journey.item.task.status", {
              context: item.task.status
            })
          )}
        </ModuleStatus>
      )}
      {isCoaching && task?.status && (
        <ModuleStatus className="as-little">
          {isInProgress && appointment
            ? translate("appointment.eventDateTime", {
                context: "detail",
                weekday,
                startTime,
                endTime,
                timeZone
              })
            : translate("journey.item.task.status", {
                context: `${task.status}${isAvailable ? "coaching" : ""}`
              })}
        </ModuleStatus>
      )}

      {!isLast && <Spacer animate={animateSpacerAfter} />}
      <SkipOrScheduleSheet
        isOpen={isSkipSheetOpen}
        onClose={handleSheetClose}
        onSkip={handleSheetSkip}
        onContinue={handleSheetContinue}
      />
    </ButtonWrap>
  );
};

export default JourneyModuleTrigger;
